import { debounce } from "@solid-primitives/scheduled";
import { useStorytellClient, ViewsSyncRequestConfig } from "@storytell/client";
import { subscribeToGlobalEvent, subscribeToServerEvent } from "@storytell/events";
import type { View } from "@storytell/types";
import { createResource, createSignal, onCleanup, onMount, startTransition } from "solid-js";
import { isServer } from "solid-js/web";
import { createResourceStorage } from "../../lib/createResourceStorage";
import { createEntitySyncManager } from "../sync/createEntitySyncManager";

export type ViewsService = ReturnType<typeof createViewsService>;

export const createViewsService = (deps: {
  authReady: () => boolean;
  authToken: () => string | undefined;
  userId: () => string | undefined;
  projectId: () => string | undefined;
}) => {
  const client = useStorytellClient();

  const views = createEntitySyncManager<View>((view) => [view.name, view.description]);

  const storage = createResourceStorage(
    () => views.all(),
    (value) => views.hydrateFromSync(value || [])
  );

  // The resource carries initialValue [], so a store that has never synced reads exactly like an
  // empty one. Anything that distinguishes "no collections" from "not fetched yet" needs this.
  const [synced, setSynced] = createSignal(false);

  // projectId is the reactive key: returning it gates the request on
  // X-Project-Id and triggers a refetch on project navigation.
  // The resource's value is consumed through `storage` below, not this binding.
  const [, { refetch }] = createResource(
    () => {
      if (isServer || !deps.authReady() || !deps.userId()) return false;
      return deps.projectId() ?? false;
    },
    async (): Promise<View[]> => {
      const request = ViewsSyncRequestConfig.withBearerToken(deps.authToken());
      const response = await client.fetch(request, undefined, undefined);
      setSynced(true);
      if (response.isErr()) {
        console.error("Failed to fetch views sync", response.error);
        return [];
      }
      setSynced(true);
      const data = Object.values(response.value.views);
      if (isServer) {
        storage[1](data);
      }
      return data;
    },
    {
      initialValue: [] as View[],
      deferStream: true,
      storage: () => storage,
    }
  );

  // Debounced refetch function wrapped in startTransition
  const debouncedRefetch = debounce(() => {
    void startTransition(() => {
      void refetch();
    });
  }, 750);

  onMount(() => {
    const unsubscribeUserContext = subscribeToGlobalEvent("user-context-changed", () => {
      void refetch();
    });
    const unsubscribeConnectivityRestored = subscribeToGlobalEvent("connectivity-restored", () => {
      void refetch();
    });
    const unsubscribeServerEvent = subscribeToServerEvent("cp.views", () => {
      debouncedRefetch();
    });
    onCleanup(() => {
      unsubscribeUserContext();
      unsubscribeConnectivityRestored();
      unsubscribeServerEvent();
      debouncedRefetch.clear();
    });
  });

  return {
    synced,
    ...views,
  };
};
